import { CommonUtils } from "./CommonUtils";
import { EmailTemplates, LoanData } from "./LoanData";

export function getEntityNameForDisplay(loanData: LoanData): string {
  if (loanData.entityInfo?.entityName) return loanData.entityInfo.entityName;
  const name = CommonUtils.concat(loanData.contactInfo?.firstName, loanData.contactInfo?.lastName, ' ');
  const defaultEntityName = 'To-Be-Formed LLC';
  if (!name) return defaultEntityName;
  return CommonUtils.concat(defaultEntityName, name, ' / ');
}

export function getDownloadFileName(documentType: string, loanData: LoanData): string {
  const prefix = documentType === 'pq-letter' ? 'Prequalification Letter' : documentType === 'loan-data' ? 'Loan Data' : 'Loan Details';
  const extn  = documentType === 'loan-data' ? 'json' : 'pdf';

  let secondPart = CommonUtils.fullAddress(loanData.propertyAddress?.line1, loanData.propertyAddress?.line2, 
    loanData.propertyAddress?.city, loanData.propertyState?.key, loanData.propertyAddress?.zip);
  if (!secondPart) {
    secondPart = loanData.propertyType?.display + ' in ' + loanData.propertyState?.display;
  }
  
  // Sanitation is important because - file system does not allow to have some special characters in file name.
  secondPart = secondPart
    .replace(/[^a-zA-Z0-9\s,-]/g, '-') // only allow a-z, A-Z, 0-9, space, comma, hyphen
    .replace(/-+/g, '-');  // collapse consecutive "-"

  return `${prefix} - ${secondPart}.${extn}`;
}

export function getEmailSubjectAndBody(loanData: LoanData, _variables?: { [key: string]: string }): { subject: string; bodyHtml: string } {
  switch (loanData?.emailTemplate) {
    case EmailTemplates.PQ_QUALIFIED:
      return qualificationEmail(loanData);
    case EmailTemplates.PQ_NOT_QUALIFIED:
      return loanData.brokerInfo?.email ? brokerDisqualificationEmail(loanData): borrowerDisqualificationEmail(loanData);
    case EmailTemplates.BROKER_DROPPED:
      return brokerDroppedEmail(loanData);
    case EmailTemplates.DID_NOT_FIND_RATE:
      return didNotFindRateEmail(loanData);
    case EmailTemplates.COMMERCIAL_THANKS:
      return commercialThanksEmail(loanData);
    case EmailTemplates.COMMERCIAL_FORMDATA:
      return commercialFormDataEmail(loanData)
    default:
      return {subject: '', bodyHtml: ''};
  }  
}

function qualificationEmail(loanData: LoanData): { subject: string; bodyHtml: string } {
  return {
    subject: `You’re Pre-Qualified – Let's Get Started on Your ${loanData.exitStrategy?.display} Loan`,
    bodyHtml: `
      <p>Hi ${getFirstName(loanData)},</p>

      <p>Great news - based on your initial information, you’ve been pre-qualified<sup>*</sup> 
      for a ${loanData.exitStrategy?.display} loan with Stormfield Capital!</p>

      <p>Your loan estimate and pre-qualification letter are attached for your reference.</p>

      <p><b>What’s Next:</b></p>

      <p>To continue the process, please <b><u>reply to this email</u></b> and we’ll send over the full application
      and document checklist as soon as possible.</p>

      <p>If you have any questions, feel free to reply to this email or <b><u>call us directly at 
      +1 203.628.4653</u></b> between 9am and 5pm, Monday to Friday.</p>

      <p>We’re excited to work with you and help bring your project to life.</p>

      ${getSignAndFooter()}`,
  };
}

export function borrowerDisqualificationEmail(loanData: LoanData): { subject: string; bodyHtml: string } {
  return {
    subject: `Update on Your ${loanData.exitStrategy?.display} Loan Request`,
    bodyHtml: `
      <p>Hi ${getFirstName(loanData)},</p>

      <p>Thank you for submitting your ${loanData.exitStrategy?.display} loan scenario to Stormfield Capital.</p>
      <p>After reviewing your submission, we’re unable to move forward at this time due to the following reason(s):</p>
      <p>${getDisqualificationReason(loanData)}</p>

      <p>We’re happy to revisit your file in the future if the scenario changes, or if you’d like
      to explore eligibility on another project.</p>

      <p>Feel free to <b><u>reply to this email</u></b> with any questions or <b><u>call us directly at +1 203.628.4653</u></b> 
      between 9am and 5pm, Monday to Friday.</p>
      
      ${getSignAndFooter()}`,
  };
}

export function brokerDisqualificationEmail(loanData: LoanData): { subject: string; bodyHtml: string } {
  return {
    subject: `This Deal Doesn't Fit – But Let’s Partner Up`,
    bodyHtml: `
      <p>Hi ${getFirstName(loanData)},</p>

      <p>Thanks for submitting your newest ${loanData.exitStrategy?.display} loan scenario!</p>
      <p>Unfortunately, the deal does not meet our lending criteria at this time due to the following reason(s):</p>
      <p>${getDisqualificationReason(loanData)}</p>

      <p>That said, we’d love to stay connected and invite you to officially <b><u>register as a broker with us.</u></b>
      We want to support you with fast reviews and reliable funding on future submissions.</p>

      <p><b>Broker Onboarding:</b></p>

      <p>Please <b><u>reply to this email</u></b> and we’ll send over:</p>
      <ul>
        <li>Our broker registration form</li>
        <li>Our submission checklist</li>
        <li>Key lending criteria and turnaround times</li>
      </ul>

      <p>Even if this one didn’t qualify, we’re here to support your business long term.</p>
      <p>Looking forward to working with you.</p>

      ${getSignAndFooter()}`,
  };
}

export function brokerDroppedEmail(loanData: LoanData): { subject: string; bodyHtml: string } {
  return {
    subject: `Thanks for Reaching Out – Let’s Schedule a Call`,
    bodyHtml: `
      <p>Hi ${getFirstName(loanData)},</p>

      <p>Thanks for reaching out to Stormfield Capital!</p>

      <p>A member of our team will follow up shortly to schedule a quick call and better understand
      how we can best work together.</p>

      <p>In the meantime, feel free to <b><u>reply to this email</u></b> with any questions or <b><u>call us directly
      at +1 203.628.4653</u></b> between 9am and 5pm, Monday to Friday.</p>

      ${getSignAndFooter()}`,
  };
}

export function didNotFindRateEmail(loanData: LoanData): { subject: string; bodyHtml: string } {
  return {
    subject: "Thanks for Reaching Out – Let’s Schedule a Call",
    bodyHtml: `
      <p>Hi ${getFirstName(loanData)},</p>

      <p>Thanks for reaching out to Stormfield Capital with your ${loanData.exitStrategy?.display} loan
      scenario! We understand that every deal doesn’t always fit into a checkbox – and we’re here to help.</p>

      <p>A member of our team will follow up shortly to schedule a quick call and better understand what
      you’re looking for.</p>

      <p>In the meantime, feel free to <b><u>reply to this email</u></b> with any questions or <b><u>call us directly at 
      +1 203.628.4653</u></b> between 9am and 5pm, Monday to Friday.</p>

      <p>Looking forward to speaking with you soon!</p>

      ${getSignAndFooter()}`,
  };
}

export function commercialThanksEmail(loanData: LoanData): { subject: string; bodyHtml: string } {
  return {
    subject: "Thanks for Reaching Out – Let’s Schedule a Call",
    bodyHtml: `
      <p>Hi ${getFirstName(loanData)},</p>

      <p>Thanks for reaching out to Stormfield Capital with your ${loanData.propertyType?.display} loan scenario!</p>

      <p>A member of our team will follow up shortly to schedule a quick call and better understand 
      what you’re looking for.</p>

      <p>In the meantime, feel free to <b><u>reply to this email</u></b> with any questions or <b><u>call us directly at 
      +1 203.628.4653</u></b> between 9am and 5pm, Monday to Friday.</p>

      <p>Looking forward to speaking with you soon!</p>

      ${getSignAndFooter()}`,
  };
}

export function commercialFormDataEmail(loanData: LoanData): {subject: string; bodyHtml: string} {
  const loanDataFileName = getDownloadFileName('loan-data', loanData).replace(/\.[^.]+$/, '');

  return {
    subject: `Commercial ${loanDataFileName}`,
    bodyHtml: `
      <p>Hi,</p>

      <p>Attached is the Commercial ${loanDataFileName}</p>

      ${getSignAndFooter()}`,
  };
}



function isBroker(loanData: LoanData): boolean {
  return !!loanData.brokerInfo?.email;
}

function getFirstName(loanData: LoanData): string {
  return (isBroker(loanData) ? loanData.brokerInfo : loanData.contactInfo)?.firstName || 'there';
}

function getDisqualificationReason(loanData: LoanData): string {
  const disqualReasons: Record<string, string> = {
    DISQUALIFIED_NO_ENTITY: `A legal entity (like an LLC) is needed to qualify.`,
    DISQUALIFIED_LOW_CREDIT_FIX: `${isBroker(loanData) ? 'Your client’s' : 'Your'} credit score is currently too low for a fix-and-flip loan.`,
    DISQUALIFIED_LOW_CREDIT_BUILD: `${isBroker(loanData) ? 'Your client’s' : 'Your'} credit score is currently too low for a new construction loan.`,
    DISQUALIFIED_NO_MULTIFAMILY_EXPERIENCE: "Multifamily experience is required to qualify for this loan.",
    DISQUALIFIED_INSUFFICIENT_BUILD_EXPERIENCE: `${isBroker(loanData) ? 'Your client needs' : 'You need'} more experience with ground-up construction projects.`,
    DISQUALIFIED_NO_FIX_EXPERIENCE: "Experience with fix-and-flip projects is required to qualify.",
    DISQUALIFIED_STATE_NOT_APPROVED: "We do not currently lend in this state.",
  };

  const status = loanData.qualificationStatus;

  let reason: string;

  if (status && disqualReasons[status]) {
    reason = disqualReasons[status];
  } else if (status === 'QUALIFIED') {
    reason = "You are qualified for a loan.";
  } else {
    reason = "We weren’t able to match you with a loan at this time.";
  }
  return reason;
}

export function getSignAndFooter(): string {
  return `
    <p style="margin-top: 0.5em; margin-bottom: 2em;">Sincerely,</p>

    <p style="margin-top: 0.1em; margin-bottom: 0.1em;">The Stormfield Capital Team</p>
    <p style="margin-top: 0.1em; margin-bottom: 0.1em;">200 Pequot Avenue</p>
    <p style="margin-top: 0.1em; margin-bottom: 0.1em;">Southport, CT 06890</p>
    <p style="margin-top: 0.1em; margin-bottom: 0.1em;">Office: +1 203.628.4653</p>
    <p style="margin-top: 0.1em; margin-bottom: 0.1em;"><a href="mailto:loans@stormfieldcapital.com" style="color: #0000EE;">loans@stormfieldcapital.com</a></p>
    <p style="margin-top: 0.1em; margin-bottom: 1.5em;"><a href="https://www.stormfieldcapital.com" target="_blank" style="color: #0000EE;">www.stormfieldcapital.com</a></p>

    <em style="color: #666; font-size: 0.8em;">
      <p>*This communication does not constitute an agreement to lend or an approval or commitment to a particular rate, fees, or term.
      Before finally approving Borrower for a loan, Lender must receive and underwrite a complete loan application from Borrower.
      Loan terms are also contingent on the returned appraisal or BPO value of the property.
      Estimates do not include third party settlement costs that may be required to close your loan.
      For more details on those potential costs, please contact your settlement agent.
      Loans may also subject to applicable taxes, insurance impounds and association fees.</p>
      
      <p>The contents of this message and its attachments, if any, are meant for the sole use of the intended recipient and may be confidential,
      privileged, or otherwise protected from disclosure. Stormfield Capital and its affiliates do not waive any confidentiality or privilege if
      this message was misdirected. This e-mail does not constitute or form part of any offer or invitation to sell, or the solicitation of an
      offer to purchase any investment and is provided for information purposes only. Stormfield Capital believes that information it provides is
      accurate and complete as at the date of publication, but do not grant any warranty of such, and Stormfield Capital, and its respective
      affiliates do not accept any liability in respect of errors or omissions. Past performance is not necessarily a guide to future results.</p>
      </em>
    `;
}
